import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  BarChart3, Bell, FileText, Users, Eye, Download, Heart, MessageCircle,
  TrendingUp, LogOut, Plus, Pencil, Trash2, X, ShieldCheck,
} from "lucide-react";
import {
  getAnalytics, getActivities, markAllRead, getBlog,
  createBlog, updateBlog, deleteBlog, apiError,
} from "@/lib/api";
import { useAdmin } from "@/context/AdminContext";

function Stat({ icon: Icon, label, value, sub }) {
  return (
    <div className="border border-zinc-800 bg-zinc-900/40 p-5" data-testid={`stat-${label.toLowerCase().replace(/\s+/g, "-")}`}>
      <div className="flex items-center gap-2 text-zinc-500 mb-2"><Icon size={15} /><span className="text-xs tracking-accent uppercase">{label}</span></div>
      <p className="font-display text-3xl font-bold">{value}</p>
      {sub && <p className="text-xs text-zinc-500 mt-1">{sub}</p>}
    </div>
  );
}

function Bars({ data, label }) {
  const max = Math.max(1, ...data.map((d) => d.count));
  return (
    <div className="border border-zinc-800 bg-zinc-900/40 p-5">
      <p className="text-xs tracking-accent uppercase text-zinc-500 mb-4">{label}</p>
      {data.length === 0 ? (
        <p className="text-sm text-zinc-600">No data yet.</p>
      ) : (
        <div className="flex items-end gap-1 h-32">
          {data.map((d) => (
            <div key={d.day} className="flex-1 flex flex-col justify-end group relative" title={`${d.day}: ${d.count}`}>
              <div className="bg-amber-500/80 group-hover:bg-amber-400 transition-colors" style={{ height: `${(d.count / max) * 100}%` }} />
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function AdminDashboard() {
  const { admin, loading, logout } = useAdmin();
  const navigate = useNavigate();
  const [tab, setTab] = useState("analytics");
  const [data, setData] = useState(null);
  const [activities, setActivities] = useState([]);
  const [posts, setPosts] = useState([]);

  const loadAll = useCallback(async () => {
    try {
      const [a, act, bl] = await Promise.all([getAnalytics(), getActivities(), getBlog()]);
      setData(a); setActivities(act); setPosts(bl);
    } catch (e) { toast.error(apiError(e)); }
  }, []);

  useEffect(() => {
    if (!loading && !admin) navigate("/admin");
  }, [loading, admin, navigate]);

  useEffect(() => { if (admin) loadAll(); }, [admin, loadAll]);

  if (loading || !admin) return <div className="pt-40 pb-40 text-center text-zinc-600">Loading…</div>;

  const unread = activities.filter((a) => !a.read).length;

  const onMarkRead = async () => {
    await markAllRead();
    setActivities((xs) => xs.map((a) => ({ ...a, read: true })));
    toast.success("Marked all as read");
  };

  const tabs = [
    { id: "analytics", label: "Analytics", icon: BarChart3 },
    { id: "activity", label: "Activity", icon: Bell, badge: unread },
    { id: "blog", label: "Blog", icon: FileText },
  ];

  return (
    <div data-testid="admin-dashboard" className="pt-32 min-h-screen">
      <section className="max-w-7xl mx-auto px-6 lg:px-8 pb-24">
        <div className="flex items-center justify-between flex-wrap gap-4 mb-10">
          <div>
            <p className="text-xs tracking-accent uppercase text-amber-500 mb-2 inline-flex items-center gap-2"><ShieldCheck size={14} /> Moderator</p>
            <h1 className="font-display text-4xl sm:text-5xl font-black tracking-tight">Dashboard</h1>
          </div>
          <div className="flex gap-3">
            <button onClick={() => navigate("/community")} className="border border-zinc-700 text-sm px-5 py-2.5 hover:border-amber-500 hover:text-amber-500 transition-colors">Community</button>
            <button data-testid="dash-logout" onClick={() => { logout(); navigate("/"); }} className="inline-flex items-center gap-2 border border-zinc-700 text-sm px-5 py-2.5 hover:border-red-500 hover:text-red-400 transition-colors"><LogOut size={15} /> Log out</button>
          </div>
        </div>

        <div className="flex gap-2 border-b border-zinc-800 mb-8">
          {tabs.map((t) => (
            <button key={t.id} data-testid={`tab-${t.id}`} onClick={() => setTab(t.id)}
              className={`relative px-5 py-3 text-sm tracking-accent uppercase transition-colors ${tab === t.id ? "text-amber-500 border-b-2 border-amber-500" : "text-zinc-500 hover:text-zinc-300"}`}>
              <span className="inline-flex items-center gap-2"><t.icon size={15} /> {t.label}</span>
              {t.badge > 0 && <span className="ml-2 bg-amber-500 text-zinc-950 text-[10px] font-bold px-1.5 py-0.5 rounded-full">{t.badge}</span>}
            </button>
          ))}
        </div>

        {tab === "analytics" && data && (
          <div className="space-y-8" data-testid="analytics-panel">
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
              <Stat icon={Users} label="Subscribers" value={data.subscribers.total} />
              <Stat icon={Eye} label="Page Views" value={data.traffic.pageviews} sub={`${data.traffic.unique_visitors} unique visitors`} />
              <Stat icon={Download} label="Dossier Downloads" value={data.conversion.dossier_downloads} />
              <Stat icon={TrendingUp} label="Conversion" value={`${data.conversion.subscriber_rate}%`} sub="visitors → subscribers" />
              <Stat icon={MessageCircle} label="Posts" value={data.community.posts} />
              <Stat icon={MessageCircle} label="Replies" value={data.community.replies} />
              <Stat icon={Heart} label="Total Likes" value={data.community.likes} />
              <Stat icon={Users} label="Community" value={data.community.posts + data.community.replies} sub="posts + replies" />
            </div>
            <div className="grid lg:grid-cols-2 gap-4">
              <Bars data={data.traffic.by_day} label="Page views (last 30 days)" />
              <Bars data={data.subscribers.by_day} label="New subscribers (last 30 days)" />
            </div>
            <div className="grid lg:grid-cols-2 gap-4">
              <div className="border border-zinc-800 bg-zinc-900/40 p-5">
                <p className="text-xs tracking-accent uppercase text-zinc-500 mb-4">Top pages</p>
                {data.traffic.top_paths.length === 0 ? <p className="text-sm text-zinc-600">No data yet.</p> :
                  <ul className="space-y-2">{data.traffic.top_paths.map((p) => (
                    <li key={p.path} className="flex justify-between text-sm"><span className="text-zinc-300 truncate">{p.path}</span><span className="text-amber-500">{p.count}</span></li>
                  ))}</ul>}
              </div>
              <div className="border border-zinc-800 bg-zinc-900/40 p-5">
                <p className="text-xs tracking-accent uppercase text-zinc-500 mb-4">Most active discussions</p>
                {data.community.top_posts.length === 0 ? <p className="text-sm text-zinc-600">No discussions yet.</p> :
                  <ul className="space-y-2">{data.community.top_posts.map((p) => (
                    <li key={p.id} className="flex justify-between text-sm gap-3"><span className="text-zinc-300 truncate">{p.title}</span><span className="text-zinc-500 whitespace-nowrap">♥ {p.likes} · 💬 {p.reply_count}</span></li>
                  ))}</ul>}
              </div>
            </div>
          </div>
        )}

        {tab === "activity" && (
          <div data-testid="activity-panel">
            <div className="flex justify-between items-center mb-5">
              <p className="text-zinc-400 text-sm">{unread} unread · {activities.length} total</p>
              <button data-testid="mark-all-read" onClick={onMarkRead} disabled={unread === 0} className="text-sm border border-zinc-700 px-4 py-2 hover:border-amber-500 hover:text-amber-500 transition-colors disabled:opacity-40">Mark all read</button>
            </div>
            {activities.length === 0 ? (
              <div className="border border-zinc-800 bg-zinc-900/40 p-12 text-center text-zinc-500">No activity yet.</div>
            ) : (
              <div className="space-y-2">
                {activities.map((a) => (
                  <div key={a.id} data-testid={`activity-${a.id}`} className={`border p-4 flex items-start gap-3 ${a.read ? "border-zinc-800 bg-zinc-900/30" : "border-amber-500/40 bg-amber-500/5"}`}>
                    <span className="text-[10px] tracking-accent uppercase text-amber-500 border border-amber-500/40 px-2 py-1 mt-0.5">{a.kind}</span>
                    <div className="flex-1 min-w-0">
                      <p className="text-zinc-200 text-sm font-medium">{a.title}</p>
                      {a.detail && <p className="text-zinc-500 text-sm truncate">{a.detail}</p>}
                      <p className="text-zinc-600 text-xs mt-1">{new Date(a.created_at).toLocaleString()}</p>
                    </div>
                    {!a.read && <span className="h-2 w-2 bg-amber-500 rounded-full mt-2" />}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {tab === "blog" && (
          <BlogManager posts={posts} reload={loadAll} />
        )}
      </section>
    </div>
  );
}

function BlogManager({ posts, reload }) {
  const [editing, setEditing] = useState(null); // null | {} (new) | post (edit)

  const onDelete = async (slug) => {
    if (!window.confirm("Delete this post?")) return;
    try { await deleteBlog(slug); toast.success("Post deleted"); reload(); }
    catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div data-testid="blog-panel">
      <div className="flex justify-between items-center mb-5">
        <p className="text-zinc-400 text-sm">{posts.length} posts</p>
        <button data-testid="new-blog" onClick={() => setEditing({})} className="inline-flex items-center gap-2 bg-amber-500 text-zinc-950 font-bold text-sm px-5 py-2.5 hover:bg-amber-400 transition-colors"><Plus size={15} /> New post</button>
      </div>
      <div className="space-y-3">
        {posts.map((p) => (
          <div key={p.slug} data-testid={`blog-row-${p.slug}`} className="border border-zinc-800 bg-zinc-900/40 p-4 flex items-center gap-4">
            <img src={p.cover_image} alt="" className="h-14 w-20 object-cover border border-zinc-800" />
            <div className="flex-1 min-w-0">
              <p className="text-zinc-200 font-medium truncate">{p.title}</p>
              <p className="text-zinc-500 text-xs">{p.category} · {new Date(p.published_at).toLocaleDateString()}</p>
            </div>
            <button data-testid={`edit-blog-${p.slug}`} onClick={() => setEditing(p)} className="text-zinc-400 hover:text-amber-500 p-2"><Pencil size={16} /></button>
            <button data-testid={`delete-blog-${p.slug}`} onClick={() => onDelete(p.slug)} className="text-red-400 hover:text-red-300 p-2"><Trash2 size={16} /></button>
          </div>
        ))}
      </div>
      {editing !== null && (
        <BlogEditor post={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); reload(); }} />
      )}
    </div>
  );
}

function BlogEditor({ post, onClose, onSaved }) {
  const isEdit = !!post.slug;
  const [form, setForm] = useState({
    title: post.title || "", excerpt: post.excerpt || "", content: post.content || "",
    cover_image: post.cover_image || "", category: post.category || "News",
  });
  const [loading, setLoading] = useState(false);
  const up = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const save = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (isEdit) await updateBlog(post.slug, form);
      else await createBlog(form);
      toast.success(isEdit ? "Post updated" : "Post published");
      onSaved();
    } catch (err) { toast.error(apiError(err)); }
    finally { setLoading(false); }
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-start justify-center overflow-y-auto bg-zinc-950/80 backdrop-blur-sm p-4 py-12" data-testid="blog-editor">
      <div className="relative w-full max-w-2xl border border-zinc-800 bg-zinc-900 p-8">
        <button onClick={onClose} className="absolute top-4 right-4 text-zinc-500 hover:text-zinc-200"><X size={20} /></button>
        <h2 className="font-display text-2xl font-bold mb-6">{isEdit ? "Edit post" : "New post"}</h2>
        <form onSubmit={save} className="space-y-4">
          <input data-testid="blog-title" required placeholder="Title" value={form.title} onChange={up("title")} className="w-full bg-zinc-950 border border-zinc-700 px-4 py-3 text-sm text-zinc-100 focus:outline-none focus:ring-1 focus:ring-amber-500" />
          <div className="grid sm:grid-cols-2 gap-4">
            <input data-testid="blog-category" placeholder="Category (e.g. News, Craft)" value={form.category} onChange={up("category")} className="bg-zinc-950 border border-zinc-700 px-4 py-3 text-sm text-zinc-100 focus:outline-none focus:ring-1 focus:ring-amber-500" />
            <input data-testid="blog-cover" placeholder="Cover image URL (optional)" value={form.cover_image} onChange={up("cover_image")} className="bg-zinc-950 border border-zinc-700 px-4 py-3 text-sm text-zinc-100 focus:outline-none focus:ring-1 focus:ring-amber-500" />
          </div>
          <textarea data-testid="blog-excerpt" required rows={2} placeholder="Short excerpt / teaser" value={form.excerpt} onChange={up("excerpt")} className="w-full bg-zinc-950 border border-zinc-700 px-4 py-3 text-sm text-zinc-100 focus:outline-none focus:ring-1 focus:ring-amber-500 resize-none" />
          <textarea data-testid="blog-content" required rows={10} placeholder="Full post content (use blank lines between paragraphs)" value={form.content} onChange={up("content")} className="w-full bg-zinc-950 border border-zinc-700 px-4 py-3 text-sm text-zinc-100 focus:outline-none focus:ring-1 focus:ring-amber-500 resize-none" />
          <button data-testid="save-blog" type="submit" disabled={loading} className="w-full bg-amber-500 text-zinc-950 font-bold text-sm px-6 py-4 hover:bg-amber-400 transition-colors disabled:opacity-60">
            {loading ? "Saving…" : isEdit ? "Save changes" : "Publish post"}
          </button>
        </form>
      </div>
    </div>
  );
}
